import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import { fetchCopperGoldYield } from '../utils/copperGoldYieldApi';
import {
  fmtSignedCorr,
  pickCorr,
  regime,
  situationAtPoint,
} from '../utils/copperGoldYieldSignal';
import CopperGoldYieldHelp from './CopperGoldYieldHelp';
import './BondDashboard.css';
import './CopperGoldYieldPanel.css';
import './StockBondReturnsPanel.css';

const RANGE_OPTIONS = ['6M', '1Y', '2Y', '5Y', 'All'];
const DEFAULT_RANGE = '5Y';
const CORR_WEEKS = [13, 26, 52];
const CORR_MIN = -1;
const CORR_MAX = 1;
const SCALE_MIN_WIDTH = 72;

const COLOR_RATIO = '#B87333';
const COLOR_YIELD = '#1763A1';
const CORR_COLORS = { 13: '#A16D17', 26: '#3A17A1', 52: '#1776A1' };

function toChartData(bars) {
  return (bars || []).map((b) => ({ time: b.time, value: Number(b.value) }));
}

function crosshairTimeString(time) {
  if (time == null) return null;
  if (typeof time === 'string') return time.slice(0, 10);
  if (typeof time === 'object' && time.year != null) {
    const m = String(time.month).padStart(2, '0');
    const d = String(time.day).padStart(2, '0');
    return `${time.year}-${m}-${d}`;
  }
  return String(time).slice(0, 10);
}

function formatChartDate(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${d}, ${y}`;
}

function CopperGoldYieldPanel() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const payloadRef = useRef(null);

  const [range, setRange] = useState(DEFAULT_RANGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);

  const situation = payload?.situation;
  const latest = payload?.latest;

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCopperGoldYield({ range });
      setPayload(data);
    } catch (err) {
      setError(err.message || 'Failed to load copper/gold vs 10Y');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!payload?.bars || !chartContainerRef.current) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      leftPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.2, bottom: 0.14 },
      },
      rightPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 360,
    });

    const ratioSeries = chart.addSeries(LineSeries, {
      color: COLOR_RATIO,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: true,
      title: 'Cu/Au',
    });
    const yieldSeries = chart.addSeries(LineSeries, {
      color: COLOR_YIELD,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: false,
      title: '10Y idx',
    });

    const corrSeries = {};
    for (const w of CORR_WEEKS) {
      corrSeries[w] = chart.addSeries(LineSeries, {
        color: CORR_COLORS[w],
        lineWidth: 1.5,
        lineStyle: 1,
        priceScaleId: 'right',
        priceLineVisible: false,
        lastValueVisible: false,
        title: `${w}w`,
      });
    }
    const zeroSeries = chart.addSeries(LineSeries, {
      color: '#4A5672',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'right',
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const { bars: b } = payload;
    ratioSeries.setData(toChartData(b.ratio_indexed));
    yieldSeries.setData(toChartData(b.yield_indexed));
    corrSeries[13].setData(toChartData(b.corr13));
    corrSeries[26].setData(toChartData(b.corr26));
    corrSeries[52].setData(toChartData(b.corr52));
    const zeroBase = b.corr26?.length ? b.corr26 : b.corr13;
    if (zeroBase?.length) {
      zeroSeries.setData(zeroBase.map((p) => ({ time: p.time, value: 0 })));
    }

    try {
      chart.priceScale('right').applyOptions({ autoScale: false, scaleMargins: { top: 0.08, bottom: 0.08 } });
      chart.priceScale('right').setVisibleRange({ from: CORR_MIN, to: CORR_MAX });
    } catch {
      /* empty */
    }
    chart.timeScale().fitContent();

    const onCrosshairMove = (param) => {
      if (!param.time || param.point == null) {
        setHoverTip(null);
        return;
      }
      const time = crosshairTimeString(param.time);
      const p = payloadRef.current?.bars;
      if (!p) {
        setHoverTip(null);
        return;
      }
      const c13 = pickCorr(p.corr13, time);
      const c26 = pickCorr(p.corr26, time);
      const c52 = pickCorr(p.corr52, time);
      if (c13 == null && c26 == null) {
        setHoverTip(null);
        return;
      }
      setHoverTip({
        x: param.point.x,
        y: param.point.y,
        time,
        ...situationAtPoint(c13, c26, c52),
      });
    };
    chart.subscribeCrosshairMove(onCrosshairMove);
    chartRef.current = chart;

    const disconnect = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height: height || 360 });
    });

    return () => {
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      disconnect();
      chart.remove();
      chartRef.current = null;
      setHoverTip(null);
    };
  }, [payload]);

  const r26 = useMemo(() => regime(latest?.correlation?.w26), [latest]);

  return (
    <section id="macro-commodities" className="bond-dash-panel copper-gold-panel">
      <div className="bond-dash-header">
        <div>
          <h3 className="bond-dash-title">Copper/gold ratio vs 10Y yield</h3>
          <p className="bond-dash-subtitle">
            {payload?.copper_symbol}/{payload?.gold_symbol} weekly (USD). Indexed ratio & 10Y vs rolling
            corr of weekly changes (13/26/52w).
            {payload?.diagnostics?.weekly_from && payload?.diagnostics?.weekly_to
              ? ` · ${payload.diagnostics.weekly_from} → ${payload.diagnostics.weekly_to}`
              : ''}
          </p>
          <CopperGoldYieldHelp />
        </div>
        {latest && (
          <div className="btc-nasdaq-legend">
            <span className="copper-gold-legend-ratio">
              Ratio {latest.ratio_indexed?.toFixed(1) ?? '—'}
            </span>
            <span className="copper-gold-legend-yield">
              10Y {latest.yield_level?.toFixed(2) ?? '—'}%
            </span>
            <span className={`copper-gold-regime copper-gold-regime--${r26.cssClass}`}>
              {r26.emoji} {r26.label} (26w)
            </span>
          </div>
        )}
      </div>

      {situation && (
        <div className="btc-nasdaq-signal-block">
          <span className={`btc-nasdaq-signal-headline copper-gold-regime--${situation.primary_css_class}`}>
            {situation.headline}
            {situation.divergence_flag ? ' ⚠️ divergence flag' : ''}
          </span>
          <p className="btc-nasdaq-signal-detail">{situation.detail}</p>
          <p className="btc-nasdaq-signal-tooltip-line">{situation.tooltip_line}</p>
        </div>
      )}

      <div className="stock-bond-toolbar">
        <span className="stock-bond-range-label">Range</span>
        {RANGE_OPTIONS.map((label) => (
          <button
            key={label}
            type="button"
            className={`stock-bond-range-btn${range === label ? ' active' : ''}`}
            onClick={() => setRange(label)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="stock-bond-charts">
        <div className="stock-bond-corr-header">
          <span className="stock-bond-corr-title">Rolling correlation (weekly Δlog ratio vs Δ10Y pp)</span>
          {latest?.correlation && (
            <div className="stock-bond-corr-legend">
              <span style={{ color: CORR_COLORS[13] }}>13w {fmtSignedCorr(latest.correlation.w13)}</span>
              <span style={{ color: CORR_COLORS[26] }}>26w {fmtSignedCorr(latest.correlation.w26)}</span>
              <span style={{ color: CORR_COLORS[52] }}>52w {fmtSignedCorr(latest.correlation.w52)}</span>
            </div>
          )}
        </div>

        {loading && (
          <div className="copper-gold-chart-placeholder">Loading copper/gold vs 10Y…</div>
        )}

        {!loading && error && (
          <div className="copper-gold-chart-placeholder copper-gold-chart-placeholder--error">
            <div>
              <strong>{error}</strong>
              <p style={{ marginTop: 8, color: 'var(--ink-2)' }}>
                Backfill: <code>python3 -m investing.backend --benchmarks-only</code> (HG=F, GLD) and
                ensure DGS10 macro: <code>--force-macro</code>
              </p>
            </div>
          </div>
        )}

        {!loading && !error && payload?.bars && (
          <div className="stock-bond-chart-wrap">
            <span className="stock-bond-axis-label stock-bond-axis-label--left">Indexed (100)</span>
            <div ref={chartContainerRef} className="stock-bond-chart stock-bond-chart-combined" />
            {hoverTip && (
              <div
                className="stock-bond-chart-tooltip"
                style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
                role="tooltip"
              >
                <div className="stock-bond-chart-tooltip-date">{formatChartDate(hoverTip.time)}</div>
                <div className={`stock-bond-chart-tooltip-regime copper-gold-regime--${hoverTip.primaryCss}`}>
                  {hoverTip.r26?.emoji} {hoverTip.headline}
                </div>
                <p className="stock-bond-chart-tooltip-body">{hoverTip.detail}</p>
              </div>
            )}
            <span className="stock-bond-axis-label stock-bond-axis-label--right">Correlation</span>
          </div>
        )}
      </div>
    </section>
  );
}

export default CopperGoldYieldPanel;
